import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { appendUtm } from '../lib/utm';
import { EditableText } from './inline/EditableText';

const REGISTRATION_URL = 'https://utconferences.eventsair.com/cee/registration/Site/Register';

// Default content (fallback if CMS data not available)
const defaultContent = {
  headline: "POWERING THE",
  headline_highlight: "NUCLEAR FUTURE",
  subheadline: "A global convening of policymakers, allied nations, industry leaders, and academia advancing nuclear deployment, energy security, and shared progress.",
  event_title: "World Nuclear Congress",
  event_tagline: "",
  event_dates: "Coming January 2027",
  host_name: "Congressional Energy Engagement",
  host_url: "https://www.usceef.org",
  venue_name: "University of Tennessee Knoxville",
  venue_url: "https://www.utk.edu",
  allied_nation: "Romania",
  primary_cta: { label: "Register Now", url: REGISTRATION_URL, style: "primary" },
  logo_url: "/assets/images/wnc-logo.png"
};

const defaultVideoConfig = {
  enabled: true,
  source_url: "/assets/videos/globe-energy.mp4",
  crossfade_enabled: true,
  crossfade_duration: 1.2,
  opacity: 0.2,
  overlay_color: "#001a2e",
  overlay_opacity: 0.4,
};

const HeroSection = ({ content, videoConfig, sectionId }) => {
  // Merge CMS content with defaults
  const data = { ...defaultContent, ...content };
  const video = { ...defaultVideoConfig, ...videoConfig };
  const scope = (path) => ({ type: 'homepage_section', sectionId, path });
  
  const [videoError, setVideoError] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Only mark the hero as errored on a genuine <video>-level failure. Source-element
  // errors (which bubble to the video's onError) fire on things like StrictMode
  // double-mount aborts, and shouldn't hide the background permanently.
  const handleMediaError = useCallback((e) => {
    const target = e?.target;
    if (target && target.tagName === 'VIDEO' && target.error && target.error.code) {
      setVideoError(true);
    }
  }, []);
  
  // Refs for dual-video crossfade
  const primaryVideoRef = useRef(null);
  const secondaryVideoRef = useRef(null);
  const [activePrimary, setActivePrimary] = useState(true);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Crossfade logic
  const handleTimeUpdate = useCallback((e) => {
    if (!video.crossfade_enabled || reducedMotion) return;
    
    const videoEl = e.target;
    const timeRemaining = videoEl.duration - videoEl.currentTime;
    
    // Skip if video is too short
    if (videoEl.duration < 5) return;
    
    // Start crossfade when approaching end
    if (timeRemaining <= video.crossfade_duration && timeRemaining > 0) {
      const otherVideo = activePrimary ? secondaryVideoRef.current : primaryVideoRef.current;
      
      if (otherVideo && otherVideo.paused) {
        otherVideo.currentTime = 0;
        otherVideo.play().catch(() => {});
      }
      
      // Calculate fade progress (0 to 1)
      const fadeProgress = 1 - (timeRemaining / video.crossfade_duration);
      
      if (activePrimary) {
        if (primaryVideoRef.current) primaryVideoRef.current.style.opacity = 1 - fadeProgress;
        if (secondaryVideoRef.current) secondaryVideoRef.current.style.opacity = fadeProgress;
      } else {
        if (secondaryVideoRef.current) secondaryVideoRef.current.style.opacity = 1 - fadeProgress;
        if (primaryVideoRef.current) primaryVideoRef.current.style.opacity = fadeProgress;
      }
    }
  }, [activePrimary, video.crossfade_enabled, video.crossfade_duration, reducedMotion]);

  const handleVideoEnded = useCallback(() => {
    if (!video.crossfade_enabled || reducedMotion) return;
    
    // Swap active video
    setActivePrimary(prev => !prev);
    
    // Reset the video that just ended
    const endedVideo = activePrimary ? primaryVideoRef.current : secondaryVideoRef.current;
    if (endedVideo) {
      endedVideo.currentTime = 0;
      endedVideo.style.opacity = 0;
    }
    
    // Ensure the new active video is fully visible
    const newActiveVideo = activePrimary ? secondaryVideoRef.current : primaryVideoRef.current;
    if (newActiveVideo) {
      newActiveVideo.style.opacity = 1;
    }
  }, [activePrimary, video.crossfade_enabled, reducedMotion]);

  // Initialize videos
  useEffect(() => {
    if (reducedMotion || !video.crossfade_enabled) return;
    
    const primary = primaryVideoRef.current;
    const secondary = secondaryVideoRef.current;
    
    if (primary) {
      primary.style.opacity = 1;
      primary.play().catch(() => {});
    }
    if (secondary) {
      secondary.style.opacity = 0;
    }
  }, [video.crossfade_enabled, reducedMotion]);

  const scrollToNext = () => {
    window.scrollTo({ 
      top: window.innerHeight, 
      behavior: 'smooth' 
    });
  };

  const handleCTAClick = () => {
    const cta = data.primary_cta;
    if (cta?.url) {
      if (cta.url.startsWith('#')) {
        const element = document.querySelector(cta.url);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (cta.url.startsWith('/')) {
        window.location.href = cta.url;
      } else {
        window.open(appendUtm(cta.url, { utm_medium: 'hero' }), '_blank');
      }
    } else {
      scrollToNext();
    }
  };

  // Use simple loop for reduced motion or when crossfade is disabled
  const useSimpleLoop = reducedMotion || !video.crossfade_enabled;
  const videoOpacity = video.opacity ?? 0.2;
  const overlayColor = video.overlay_color || "#001a2e";
  const overlayOpacity = video.overlay_opacity ?? 0.4;

  // Convert #rrggbb -> rgba(r,g,b,a) for the overlay
  const overlayRgba = (() => {
    const m = /^#?([0-9a-f]{6})$/i.exec(overlayColor);
    if (!m) return `rgba(0, 26, 46, ${overlayOpacity})`;
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${overlayOpacity})`;
  })();

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#001a2e] via-[#00457f] to-[#003461]">
      {/* Config-driven color overlay — always rendered so admin sliders take effect
          even when video is disabled or fails to load */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ backgroundColor: overlayRgba }}
        data-testid="hero-color-overlay"
      />
      {/* Video Background with Crossfade */}
      {video.enabled && !videoError && (
        <div className="absolute inset-0 z-0">
          {useSimpleLoop ? (
            // Simple single video loop for reduced motion
            <>
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
                style={{ opacity: videoOpacity }}
                onError={handleMediaError}
              >
                <source src={video.source_url} type="video/mp4" />
              </video>
              <div 
                className="absolute inset-0 pointer-events-none" 
                style={{ backgroundColor: overlayRgba }}
                data-testid="hero-color-overlay-simple"
              />
            </>
          ) : (
            // Dual-video crossfade system
            <>
              <video
                ref={primaryVideoRef}
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out"
                style={{ opacity: 1 }}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnded}
                onError={handleMediaError}
              >
                <source src={video.source_url} type="video/mp4" />
              </video>
              <video
                ref={secondaryVideoRef}
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out"
                style={{ opacity: 0 }}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnded}
              >
                <source src={video.source_url} type="video/mp4" />
              </video>
              {/* Color overlay driven by admin sliders */}
              <div 
                className="absolute inset-0 pointer-events-none" 
                style={{ backgroundColor: overlayRgba }}
                data-testid="hero-color-overlay"
              />
            </>
          )}
        </div>
      )}

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#00457f]/80 via-transparent to-transparent z-0"></div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 flex flex-col items-center text-center">
        {/* Main Tagline */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-tight">
          <EditableText as="span" scope={scope('headline')} value={data.headline} multiline={false}>{data.headline}</EditableText>
          <br />
          <EditableText as="span" scope={scope('headline_highlight')} value={data.headline_highlight} multiline={false} className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500">{data.headline_highlight}</EditableText>
        </h1>

        {/* Subheading */}
        <p className="text-xl md:text-2xl text-gray-200 font-light mb-3 max-w-3xl">
          <EditableText as="span" scope={scope('subheadline')} value={data.subheadline}>{data.subheadline}</EditableText>
        </p>

        {/* Event Details Box */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 mb-10 shadow-2xl max-w-4xl">
          {/* Logo and Event Details Row */}
          <div className="flex flex-col md:flex-row items-center gap-6 mb-4">
            {/* Logo - Left Aligned */}
            {data.logo_url && (
              <div className="flex-shrink-0">
                <img
                  src={data.logo_url}
                  alt={data.event_title}
                  className="h-32 md:h-40 w-auto"
                />
              </div>
            )}
            
            {/* Event Info - Right Side, Vertically Centered */}
            <div className="flex flex-col justify-center text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                <EditableText as="span" scope={scope('event_title')} value={data.event_title} multiline={false}>{data.event_title}</EditableText>
              </h2>
              {(data.event_tagline || true) && (
                <p className="text-sm md:text-base text-gray-300 font-light italic mb-2">
                  <EditableText as="span" scope={scope('event_tagline')} value={data.event_tagline} placeholder="Add tagline…">{data.event_tagline}</EditableText>
                </p>
              )}
              <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-transparent bg-clip-text">
                <EditableText as="span" scope={scope('event_dates')} value={data.event_dates} multiline={false}>{data.event_dates}</EditableText>
              </p>
            </div>
          </div>
          
          {/* Host Information - Below Both */}
          <div className="border-t border-white/20 pt-4 text-center text-gray-200">
            <p className="text-sm md:text-base">
              Hosted By{' '}
              <EditableText as="span" scope={scope('host_name')} value={data.host_name} multiline={false} className="text-white font-semibold">{data.host_name}</EditableText>
            </p>
            <p className="text-sm md:text-base mt-1">
              Venue:{' '}
              <EditableText as="span" scope={scope('venue_name')} value={data.venue_name} multiline={false} className="text-white font-semibold">{data.venue_name}</EditableText>
            </p>
            {data.allied_nation && (
              <p className="text-sm md:text-base mt-1">
                WNC Allied Nation — <EditableText as="span" scope={scope('allied_nation')} value={data.allied_nation} multiline={false} className="text-white font-semibold">{data.allied_nation}</EditableText>
              </p>
            )}
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleCTAClick}
          className="group bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 hover:from-yellow-600 hover:via-amber-600 hover:to-yellow-700 text-white text-lg font-bold px-10 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center gap-2 relative overflow-hidden"
          style={{
            boxShadow: '0 10px 40px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
          }}
        >
          <span className="relative z-10 flex items-center gap-2">
            <EditableText as="span" scope={scope('primary_cta.label')} value={data.primary_cta?.label} multiline={false}>{data.primary_cta?.label || "Get Updates & Register"}</EditableText>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-0 right-0 flex justify-center z-10">
        <button 
          onClick={scrollToNext}
          className="animate-bounce"
        >
          <ChevronDown className="w-8 h-8 text-white opacity-70 hover:opacity-100 transition-opacity" />
        </button>
      </div>
    </div>
  );
};

export default HeroSection;